import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Grid,
  Box,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Divider
} from "@mui/material";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const EmployeeProfile = () => {
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [profileData, setProfileData] = useState({
    personalDetails: {
      firstName: "",
      lastName: "",
      personalemail: "",
      dateOfBirth: "",
      anniversary: "",
      gender: "",
      panNumber: "",
      adharCardNumber: "",
    },
    contactInfo: {
      phoneNumber: "",
      houseNumber: "",
      street: "",
      state: "",
      area: "",
      city: "",
      pinCode: "",
    },
    emergencyContact: {
      mobile: "",
      landline: "",
    },
    employmentDetails: {
      employeeId: "",
      companyemail: "",
      department: "",
      designation: "",
      joiningDate: "",
      employmentStatus: "",
      grade: "",
    },
    financialDetails: {
      costCenter: "",
      bankName: "",
      accountHolderName: "",
      accountNumber: "",
      accountType: "",
      branch: "",
      ifscCode: "",
      currentSalary: "",
    },
    roleInfo: {
      roleType: "",
      reportingManager: "",
      responsibilities: []
    },
    qualifications: [],
    certificates: []
  });

  const editableFields = {
    contactInfo: ["phoneNumber", "houseNumber", "street", "state", "area", "city", "pinCode"],
    emergencyContact: ["mobile", "landline"],
    personalDetails: ["personalemail", "anniversary", "panNumber", "adharCardNumber"]
  };

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        setLoading(true);

        const api = axios.create({
          baseURL: `${API_URL}`,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          withCredentials: true
        });

        const [personalResponse, financialResponse, roleResponse, cmpRes] = await Promise.all([
          api.get(`/api/profile`),
          api.get(`/api/financial`),
          api.get(`/api/role`),
          api.get(`/api/cmp/${company_id}`).catch(() => ({ data: { data: null } }))
        ]);
        
        if (cmpRes?.data?.data) {
          setCompanyConfig(cmpRes.data.data);
        }

        if (personalResponse.data.success) {
          const employeeData = personalResponse.data.data;
          setProfileData(prev => ({
            ...prev,
            personalDetails: {
              firstName: employeeData.firstName || "",
              lastName: employeeData.lastName || "",
              personalemail: employeeData.personalemail || "",
              dateOfBirth: employeeData.dateOfBirth?.split('T')[0] || '',
              anniversary: employeeData.anniversary?.split('T')[0] || '',
              gender: employeeData.gender || "",
              panNumber: employeeData.panNumber || "",
              adharCardNumber: employeeData.adharCardNumber || ""
            },
            contactInfo: {
              phoneNumber: employeeData.phoneNumber || "",
              houseNumber: employeeData.houseNumber || "",
              street: employeeData.street || "",
              state: employeeData.state || "",
              area: employeeData.area || "",
              city: employeeData.city || "",
              pinCode: employeeData.pinCode || ""
            },
            emergencyContact: {
              mobile: employeeData.mobile || "",
              landline: employeeData.landline || "",
            },
            employmentDetails: {
              employeeId: employeeData.employee_id || "",
              companyemail: employeeData.companyemail || "",
              department: employeeData.department || "",
              employmentStatus: employeeData.employmentStatus || ""
            },
            qualifications: employeeData.qualifications || [],
            certificates: employeeData.certificates || []
          }));
        }

        if (financialResponse.data.success) {
          const financialData = financialResponse.data.data;
          setProfileData(prev => ({
            ...prev,
            financialDetails: {
              costCenter: financialData.costCenter ? `${financialData.costCenter.name} (${financialData.costCenter.code})` : "Not Assigned",
              bankName: financialData.bankName || "",
              accountHolderName: financialData.accountHolderName || "",
              accountNumber: financialData.accountNumber || "",
              accountType: financialData.accountType || "",
              branch: financialData.branch || "",
              ifscCode: financialData.ifscCode || "",
              currentSalary: financialData.currentSalary || ""
            }
          }));
        }

        if (roleResponse.data.success) {
          const roleData = roleResponse.data.data;
          setProfileData(prev => ({
            ...prev,
            employmentDetails: {
              ...prev.employmentDetails,
              designation: roleData.designation || "",
              joiningDate: roleData.joiningDate?.split('T')[0] || "",
              grade: roleData.grade || ""
            },
            roleInfo: {
              roleType: roleData.roleType || "",
              reportingManager: roleData.reportingManager || "",
              responsibilities: [
                ...(roleData.selectedResponsibilities || []),
                ...(roleData.additionalResponsibilities ? [roleData.additionalResponsibilities] : [])
              ]
            }
          }));
        }

        if (!personalResponse.data.success && !financialResponse.data.success && !roleResponse.data.success) {
          showAlert("Could not find your employee data. Please contact HR.", "error");
        }

      } catch (error) {
        //console.error("Error fetching employee data:", error);
        showAlert("Failed to load your profile information. Please try again later.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, []);

  const handleChange = (e, section, field) => {
    setProfileData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: e.target.value
      }
    }));
  };

  const isEditable = (section, field) => {
    return editableFields[section] && editableFields[section].includes(field);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const payloadcompany = JSON.parse(atob(token.split('.')[1]));
      const company_id = payloadcompany.company_id;
      setLoading(true);

      const payload = {
        personalemail: profileData.personalDetails.personalemail,
        anniversary: profileData.personalDetails.anniversary || null,
        phoneNumber: profileData.contactInfo.phoneNumber,
        houseNumber: profileData.contactInfo.houseNumber,
        street: profileData.contactInfo.street,
        state: profileData.contactInfo.state,
        panNumber: profileData.personalDetails.panNumber,
        adharCardNumber: profileData.personalDetails.adharCardNumber,
        area: profileData.contactInfo.area,
        city: profileData.contactInfo.city,
        pinCode: profileData.contactInfo.pinCode,
        mobile: profileData.emergencyContact.mobile,
        landline: profileData.emergencyContact.landline,
      };

      const employeeId = profileData.employmentDetails.employeeId;
      if (!employeeId) {
        showAlert("Employee ID not found", "error");
        setLoading(false);
        return;
      }

      const response = await axios.put(
        `${API_URL}/api/update/EmployeeProfile/${employeeId}`,
        payload,
        {
          headers: { "Content-Type": "application/json" , Authorization: `Bearer ${token}`},
          
        }
      );

      if (response.data.success) {
        showAlert("Profile updated successfully!");
      } else {
        showAlert("Failed to update profile.", "error");
      }
    } catch (error) {
      //console.error("Update error:", error);
      showAlert(`Error updating profile: ${error.response?.data?.message || error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1500, margin: 'auto', padding: 3 }}>
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alertSeverity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>

      <Typography variant="h4" gutterBottom align="center" fontWeight="bold" sx={{ color: '#1a237e', mb: 4 }}>
        Employee Profile
      </Typography>

      <Paper elevation={3} sx={{ borderRadius: 3, mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, v) => setCurrentTab(v)}
          variant="fullWidth"
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Personal & Work Info" />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <Paper elevation={0} sx={{ padding: 2, marginBottom: 3, border: '1px solid #f0f0f0' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#1a237e', fontWeight: 'bold' }}>Employment Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Employee ID"
                  fullWidth
                  value={profileData.employmentDetails.employeeId}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Company Email"
                  fullWidth
                  value={profileData.employmentDetails.companyemail}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Designation"
                  fullWidth
                  value={profileData.employmentDetails.designation}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Joining Date"
                  fullWidth
                  value={profileData.employmentDetails.joiningDate}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Employment Status"
                  fullWidth
                  value={profileData.employmentDetails.employmentStatus}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Grade"
                  fullWidth
                  value={profileData.employmentDetails.grade}
                  disabled={true}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
            <Typography variant="h6" gutterBottom>Personal Details</Typography>
            <Grid container spacing={2}>
              {Object.keys(profileData.personalDetails)
                .filter(field => field !== 'adharCardNumber' || (!companyConfig?.country || companyConfig.country.toLowerCase() === 'india'))
                .map((field) => {
                  let label = field.replace(/([A-Z])/g, " $1").charAt(0).toUpperCase() + field.replace(/([A-Z])/g, " $1").slice(1).trim();
                  if (field === 'panNumber') {
                    label = companyConfig?.nationalIdLabel || 'PAN Number';
                  } else if (field === 'adharCardNumber') {
                    label = 'Aadhar Card Number';
                  }
                  
                  return (
                    <Grid item xs={6} key={field}>
                      <TextField
                        label={label}
                        fullWidth
                        type={field === "dateOfBirth" || field === "anniversary" ? "date" : "text"}
                        InputLabelProps={field === "dateOfBirth" || field === "anniversary" ? { shrink: true } : {}}
                        value={profileData.personalDetails[field]}
                        onChange={(e) => handleChange(e, "personalDetails", field)}
                      />
                    </Grid>
                  );
              })}
            </Grid>
          </Paper>

          <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
            <Typography variant="h6" gutterBottom>Contact Information</Typography>
            <Grid container spacing={2}>
              {Object.keys(profileData.contactInfo).map((field) => (
                <Grid item xs={6} key={field}>
                  <TextField
                    label={field.replace(/([A-Z])/g, " $1").charAt(0).toUpperCase() + field.replace(/([A-Z])/g, " $1").slice(1).trim()}
                    fullWidth
                    value={profileData.contactInfo[field]}
                    onChange={(e) => handleChange(e, "contactInfo", field)}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
            <Typography variant="h6" gutterBottom>Emergency Contact</Typography>
            <Grid container spacing={2}>
              {Object.keys(profileData.emergencyContact).map((field) => (
                <Grid item xs={6} key={field}>
                  <TextField
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    fullWidth
                    value={profileData.emergencyContact[field]}
                    onChange={(e) => handleChange(e, "emergencyContact", field)}
                    disabled={!isEditable("emergencyContact", field)}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
            <Typography variant="h6" gutterBottom>Financial Details</Typography>
            <Grid container spacing={2}>
              {Object.keys(profileData.financialDetails).map((field) => (
                <Grid item xs={6} key={field}>
                  <TextField
                    label={field.replace(/([A-Z])/g, " $1").charAt(0).toUpperCase() + field.replace(/([A-Z])/g, " $1").slice(1).trim()}
                    fullWidth
                    value={field === "currentSalary" ? `${companyConfig?.currency_symbol || '₹'}${profileData.financialDetails[field]}` : profileData.financialDetails[field]}
                    disabled={true}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
            <Typography variant="h6" gutterBottom>Role & Responsibilities</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Role Type"
                  fullWidth
                  value={profileData.roleInfo.roleType}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Reporting Manager"
                  fullWidth
                  value={profileData.roleInfo.reportingManager}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                  Responsibilities:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {profileData.roleInfo.responsibilities.map((resp, index) => (
                    <Chip key={index} label={resp} color="primary" variant="outlined" />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              size="large"
              sx={{ borderRadius: 2, px: 5 }}
            >
              Update Profile
            </Button>
          </Box>
        </TabPanel>

      </Paper>
    </Box>
  );
};

export default EmployeeProfile;